import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Mail, Sparkles, ArrowRight, Loader2 } from "lucide-react";
import { fetchBuddies, submitEmail, pilotStats } from "../lib/api";
import { toast } from "sonner";

export default function Footer() {
  const [buddies, setBuddies] = useState([]);
  const [stats, setStats] = useState(null);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchBuddies().then(setBuddies).catch((err) => console.warn("[fetch] failed:", err));
    pilotStats().then(setStats).catch((err) => console.warn("[fetch] failed:", err));
  }, []);

  const onJoin = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast.error("Please enter a valid email.");
      return;
    }
    setLoading(true);
    try {
      await submitEmail(email, "footer_join");
      toast.success("You're in the family!");
      setEmail("");
    } catch {
      toast.error("Couldn't subscribe. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="footer" className="relative mt-12 overflow-hidden">
      {/* Buddy parade sticker band */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-b from-pb-cream to-transparent h-16 z-10 pointer-events-none" />
        <div className="overflow-hidden py-8 bg-pb-cream">
          <div className="flex gap-10 animate-[scroll_30s_linear_infinite] items-center" style={{ animationName: "scroll" }}>
            {[...buddies, ...buddies, ...buddies].slice(0, 18).map((b, i) => (
              <div
                key={`${b.id}-${i}`}
                className={`flex items-center gap-3 flex-shrink-0 ${i % 2 ? "rotate-[3deg]" : "-rotate-[3deg]"}`}
              >
                <div className="h-14 w-14 rounded-full overflow-hidden bg-white border-4 border-white shadow-[0_8px_18px_rgba(28,22,40,0.18)]">
                  <img src={b.image} alt={b.name} className="w-full h-full object-cover" />
                </div>
                <span className="font-display font-black text-pb-ink whitespace-nowrap text-lg">{b.name}</span>
                <span className="text-3xl text-pb-coral/40">✦</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Deep Purple body — matches the HowItWorks + marquee gradient
          so the footer reads as one continuous brand surface. */}
      <div className="relative bg-gradient-to-br from-pb-brand-purple via-[#3A1F6B] to-pb-brand-ink">
        {/* Supporting Buddy Teal halo on the right — brand accent only. */}
        <div
          aria-hidden
          className="absolute -top-20 -right-20 w-[460px] h-[460px] rounded-full opacity-20 pointer-events-none"
          style={{ background: "radial-gradient(circle, #06A5A1 0%, transparent 65%)", filter: "blur(40px)" }}
        />

        <div className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 pt-16 pb-12 text-white">
          {/* JOIN block — single closing CTA */}
          <div id="join" className="text-center max-w-2xl mx-auto scroll-mt-24">
            <span className="inline-flex items-center gap-2 rounded-full bg-pb-brand-teal/15 backdrop-blur px-4 py-1.5 text-[11px] font-extrabold uppercase tracking-widest text-pb-brand-teal border border-pb-brand-teal/40">
              <Sparkles className="h-3.5 w-3.5" /> Join the family
            </span>
            <h2 className="font-display font-black text-4xl sm:text-5xl mt-5 leading-[1.05]">
              Recipes, character stories,<br/> and the launch list — straight to your inbox.
            </h2>

            <form data-testid="footer-join-form" onSubmit={onJoin} className="mt-7 flex flex-col sm:flex-row gap-2 max-w-md mx-auto">
              <div className="flex-1 relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-pb-coral" />
                <input
                  data-testid="footer-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@family.com"
                  className="w-full rounded-2xl bg-white/95 text-pb-ink font-bold pl-11 pr-4 py-3.5 placeholder:text-pb-soft/60 focus:outline-none focus:ring-4 focus:ring-pb-sun/40 transition"
                />
              </div>
              <button
                data-testid="footer-join-btn"
                type="submit"
                disabled={loading}
                className="rounded-2xl bg-pb-coral text-white font-extrabold px-6 py-3.5 hover:scale-105 transition shadow-[0_10px_24px_rgba(229,72,77,0.4)] disabled:opacity-60 inline-flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Join
              </button>
            </form>

            {stats?.spots_remaining > 0 && (
              <p className="mt-5 text-sm text-white/75">
                <span className="text-pb-sun">★</span> Want more involvement?{" "}
                <Link to="/pilot" data-testid="footer-pilot-link" className="text-pb-sun font-extrabold underline-offset-2 hover:underline">
                  {stats.spots_remaining} Founding Family spots open
                </Link>
                {" "}— free Premium Kit included.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Dark purple bottom strip — continues the gradient surface above
          with a crisp white-rule divider so it reads as one footer. */}
      <div className="relative bg-pb-brand-ink border-t border-white/10">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-5">
            {/* Brand */}
            <div className="flex-shrink-0 text-center md:text-left">
              <img
                src="/brand/logo-characters.png"
                alt="Plate Buddies"
                className="h-28 sm:h-32 w-auto max-w-none object-contain mx-auto md:mx-0"
              />
            </div>

            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs font-bold text-white/70">
              <Link to="/shop" data-testid="footer-link-shop" className="hover:text-white transition">Kits</Link>
              <Link to="/journal" data-testid="footer-link-journal" className="hover:text-white transition">Journal</Link>
              <Link to="/create" className="hover:text-white transition">Make a Buddy</Link>
              <Link to="/planner" className="hover:text-white transition">Meal Planner</Link>
              <Link to="/library" className="hover:text-white transition">My Library</Link>
              <Link
                to="/investors"
                data-testid="footer-link-investors"
                className="hover:text-white transition"
              >
                Investors
              </Link>
              <Link to="/pilot" className="hover:text-white transition">Founding Families</Link>
              <a href="mailto:hello@platebuddies.com" className="hover:text-white transition">Contact</a>
              <Link to="/privacy" data-testid="footer-link-privacy" className="hover:text-white transition">Privacy</Link>
              <Link to="/terms" data-testid="footer-link-terms" className="hover:text-white transition">Terms</Link>
              <Link to="/refunds" data-testid="footer-link-refunds" className="hover:text-white transition">Refunds</Link>
            </div>

            <div className="flex gap-2">
              <a
                href="https://www.instagram.com/plate_buddies"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-social-instagram"
                aria-label="Plate Buddies on Instagram"
                className="h-10 w-10 rounded-2xl bg-white/10 hover:bg-white/20 text-white transition flex items-center justify-center"
              >
                <Instagram className="h-4 w-4" />
              </a>
              <a
                href="https://www.facebook.com/platebuddies"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-social-facebook"
                aria-label="Plate Buddies on Facebook"
                className="h-10 w-10 rounded-2xl bg-white/10 hover:bg-white/20 text-white transition flex items-center justify-center"
              >
                <Facebook className="h-4 w-4" />
              </a>
              <a
                href="https://www.tiktok.com/@platebuddies"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-social-tiktok"
                aria-label="Plate Buddies on TikTok"
                className="h-10 w-10 rounded-2xl bg-white/10 hover:bg-white/20 text-white transition flex items-center justify-center"
              >
                <svg
                  viewBox="0 0 24 24"
                  className="h-4 w-4"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.84 20.1a6.34 6.34 0 0 0 10.86-4.43V8.99a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.88-.42z" />
                </svg>
              </a>
            </div>
          </div>

          <p className="mt-6 text-center text-[11px] text-white/50">
            © {new Date().getFullYear()} Plate Buddies. Hand-illustrated with love. Made for happier dinners.
          </p>
        </div>
      </div>
      <style>{`
        @keyframes scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </footer>
  );
}
